import React, { useState } from 'react';
import { apiService } from '../services/api';
import { useApi, useMutation } from '../hooks/useApi';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { UserCircle, ShieldCheck, CheckCircle, XCircle, Boxes, Palette, ListChecks, Ruler } from 'lucide-react';

function Dashboard() {
  const { user, roli } = useAuth();
  const { getShopName } = useSettings();
  const [syncProductId, setSyncProductId] = useState('');
  const [syncNgjyraId, setSyncNgjyraId] = useState('');
  const [syncKategoriId, setSyncKategoriId] = useState('');
  const [syncMadhesiId, setSyncMadhesiId] = useState('');

  const { data: top5 = [], loading: loadingTop5 } = useApi(
    () => apiService.get('/raport-top5'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: bottom5 = [], loading: loadingBottom5 } = useApi(
    () => apiService.get('/raport-bottom5'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: lowStock = [], loading: loadingLowStock } = useApi(
    () => apiService.get('/raport-low-stock'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { mutate: syncWoo, loading: syncLoading } = useMutation(
    async ({ type, productId }) => {
      let url = '';
      if (type === 'pull') {
        url = '/sync/woo/pull-orders';
      } else if (type === 'push') {
        url = '/sync/woo/push-products';
      } else if (type === 'push-one') {
        if (!productId) {
          throw new Error('Sheno ID e produktit.');
        }
        url = `/sync/woo/push-product/${productId}`;
      }

      const config = {};
      if (type === 'push' || type === 'push-one') {
        config.headers = { 'X-Allow-Attribute-Create': '1' };
      }

      return await apiService.post(url, {}, config);
    },
    {
      successMessage: 'Sync u krye me sukses!',
      onError: (err) => {
        logger.error('Sync error:', err);
      }
    }
  );

  const handleSync = (type) => {
    if (type === 'push-one' && !syncProductId) {
      return;
    }
    syncWoo({ type, productId: syncProductId });
  };

  const { mutate: syncWooNgjyra, loading: syncNgjyraLoading } = useMutation(
    async ({ type, ngjyraId }) => {
      let url = '';
      if (type === 'push-one') {
        url = `/sync/woo/push-ngjyra/${ngjyraId}`;
      } else if (type === 'push-all') {
        url = '/sync/woo/push-all-ngjyrat';
      }
      return await apiService.post(url);
    },
    {
      successMessage: 'Sync u krye me sukses!',
      onError: (err) => {
        logger.error('Sync error:', err);
      }
    }
  );

  const { mutate: syncWooKategori, loading: syncKategoriLoading } = useMutation(
    async ({ type, kategoriId }) => {
      let url = '';
      if (type === 'push-one') {
        url = `/sync/woo/push-kategori/${kategoriId}`;
      } else if (type === 'push-all') {
        url = '/sync/woo/push-all-kategorite';
      }
      return await apiService.post(url);
    },
    {
      successMessage: 'Sync u krye me sukses!',
      onError: (err) => {
        logger.error('Sync error:', err);
      }
    }
  );

  const handleSyncNgjyra = (type) => {
    if (type === 'push-one' && !syncNgjyraId) {
      alert('Ju lutem shkruani ID e ngjyrës');
      return;
    }
    syncWooNgjyra({ type, ngjyraId: syncNgjyraId });
  };

  const handleSyncKategori = (type) => {
    if (type === 'push-one' && !syncKategoriId) {
      alert('Ju lutem shkruani ID e kategorisë');
      return;
    }
    syncWooKategori({ type, kategoriId: syncKategoriId });
  };

  const { mutate: syncWooMadhesi, loading: syncMadhesiLoading } = useMutation(
    async ({ type, madhesiId }) => {
      let url = '';
      if (type === 'push-one') {
        url = `/sync/woo/push-madhesi/${madhesiId}`;
      } else if (type === 'push-all') {
        url = '/sync/woo/push-all-madhesite';
      }
      return await apiService.post(url);
    },
    {
      successMessage: 'Sync u krye me sukses!',
      onError: (err) => {
        logger.error('Sync error:', err);
      }
    }
  );

  const handleSyncMadhesi = (type) => {
    if (type === 'push-one' && !syncMadhesiId) {
      alert('Ju lutem shkruani ID e madhësisë');
      return;
    }
    syncWooMadhesi({ type, madhesiId: syncMadhesiId });
  };

  const Panel = ({ title, data, icon, color }) => {
    const colorConfig = {
      green: {
        text: 'text-green-700 dark:text-green-300',
        bg: 'bg-green-100 dark:bg-green-900/30',
        border: 'border-green-200 dark:border-green-800',
        accent: 'text-green-600 dark:text-green-400',
      },
      red: {
        text: 'text-red-700 dark:text-red-300',
        bg: 'bg-red-100 dark:bg-red-900/30',
        border: 'border-red-200 dark:border-red-800',
        accent: 'text-red-600 dark:text-red-400',
      },
      yellow: {
        text: 'text-primary-700 dark:text-primary-300',
        bg: 'bg-primary-100 dark:bg-primary-900/30',
        border: 'border-primary-200 dark:border-primary-800',
        accent: 'text-primary-600 dark:text-primary-400',
      },
    };
    const colors = colorConfig[color] || colorConfig.yellow;

    return (
      <div className="card-premium-hover p-5 md:p-6 animate-fade-in">
        <h3 className={`text-lg font-bold mb-5 flex items-center gap-3 ${colors.text}`}>
          <span className={`p-2 ${colors.bg} ${colors.border} border rounded-lg`}>{icon}</span>
          <span>{title}</span>
        </h3>
        <ul className="space-y-3">
          {data.length > 0 ? (
            data.map((item, idx) => (
              <li key={item.id} className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-dark-700 last:border-0 group">
                <span className="max-w-[70%] break-words whitespace-normal block text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  <span className={`font-bold ${colors.accent} mr-2`}>{idx + 1}.</span>
                  {item.emri}
                </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-dark-700 px-3 py-1 rounded-lg text-sm">
                  {item.total || item.total_stok} {item.total_stok !== undefined ? 'në stok' : 'shitje'}
                </span>
              </li>
            ))
          ) : (
            <li className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Nuk ka të dhëna</li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className="w-full animate-fade-in">
      {/* Header Section */}
      <div className="mb-10 text-center animate-slide-down">
        <h1 className="text-4xl md:text-5xl font-bold text-gradient-logo mb-4">
          Mirë se vini në {getShopName()}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          Paneli i kontrollit për menaxhimin e shitjeve dhe produkteve
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <div className="card-premium-hover p-6 flex items-center gap-5 animate-fade-in">
          <div className="p-4 bg-gradient-primary rounded-2xl shadow-premium dark:shadow-glow-yellow-dark">
            <UserCircle size={32} className="text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">Përdoruesi aktiv</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{user}</p>
          </div>
        </div>
        <div className="card-premium-hover p-6 flex items-center gap-5 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="p-4 bg-gradient-secondary rounded-2xl shadow-premium dark:shadow-glow-blue-dark">
            <ShieldCheck size={32} className="text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">Roli</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100 capitalize">{roli}</p>
          </div>
        </div>
        <div className="card-premium-hover p-6 flex items-center gap-5 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="p-4 bg-gradient-logo rounded-2xl shadow-premium dark:shadow-glow-yellow-dark">
            <CheckCircle size={32} className="text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">Statusi</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">Aktiv</p>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="card-premium p-8 mb-12 bg-gradient-to-r from-primary-50 via-secondary-50 to-primary-50 dark:from-primary-900/20 dark:via-secondary-900/20 dark:to-primary-900/20 border-primary-200 dark:border-primary-800 animate-slide-up">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Jeni kyçur me sukses! 🎉</h2>
          {roli === 'admin' ? (
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              Si administrator, menaxhoni produktet, përdoruesit dhe raportet.
            </p>
          ) : roli === 'shites' ? (
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              Si shitës, regjistroni shitje dhe kontrolloni stokun.
            </p>
          ) : (
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              Roli juaj është i kufizuar. Kontaktoni administratorin për më shumë qasje.
            </p>
          )}
        </div>
      </div>

      {/* Reports Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <Panel title="Top 5 më të shitura" data={Array.isArray(top5) ? top5 : (top5?.data || [])} icon={<CheckCircle size={20} />} color="green" />
        <Panel title="Top 5 më pak të shitura" data={Array.isArray(bottom5) ? bottom5 : (bottom5?.data || [])} icon={<XCircle size={20} />} color="red" />
        <Panel title="Stoku nën 5 njësi" data={Array.isArray(lowStock) ? lowStock : (lowStock?.data || [])} icon={<Boxes size={20} />} color="yellow" />
      </div>

      {/* WooCommerce Sync - Admin Only */}
      {roli === 'admin' && (
        <div className="card-premium p-8 animate-slide-up">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Boxes size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            WooCommerce Sync
          </h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleSync('pull')}
              disabled={syncLoading}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {syncLoading ? 'Duke tërhequr...' : 'Tërhiq Porositë'}
            </button>
            <button
              onClick={() => handleSync('push')}
              disabled={syncLoading}
              className="btn-premium-secondary-gradient text-sm disabled:opacity-50"
            >
              {syncLoading ? 'Duke transferuar...' : 'Transfero Produktet'}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <input
                value={syncProductId}
                onChange={(e) => setSyncProductId(e.target.value)}
                placeholder="ID e Produktit"
                className="input-premium flex-1 text-sm"
              />
              <button
                onClick={() => handleSync('push-one')}
                disabled={syncLoading}
                className="btn-premium-secondary text-sm bg-dark-700 dark:bg-dark-600 text-white hover:bg-dark-800 dark:hover:bg-dark-500 disabled:opacity-50"
              >
                Transfero Një
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ngjyrat Sync - Admin Only */}
      {roli === 'admin' && (
        <div className="card-premium p-8 animate-slide-up">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Palette size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            Ngjyrat Sync
          </h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleSyncNgjyra('push-all')}
              disabled={syncNgjyraLoading}
              className="btn-premium-secondary-gradient text-sm disabled:opacity-50"
            >
              {syncNgjyraLoading ? 'Duke transferuar...' : 'Transfero Të Gjitha Ngjyrat'}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <input
                value={syncNgjyraId}
                onChange={(e) => setSyncNgjyraId(e.target.value)}
                placeholder="ID e Ngjyrës"
                className="input-premium flex-1 text-sm"
              />
              <button
                onClick={() => handleSyncNgjyra('push-one')}
                disabled={syncNgjyraLoading}
                className="btn-premium-secondary text-sm bg-dark-700 dark:bg-dark-600 text-white hover:bg-dark-800 dark:hover:bg-dark-500 disabled:opacity-50"
              >
                Transfero Një
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kategoritë Sync - Admin Only */}
      {roli === 'admin' && (
        <div className="card-premium p-8 animate-slide-up">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <ListChecks size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            Kategoritë Sync
          </h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleSyncKategori('push-all')}
              disabled={syncKategoriLoading}
              className="btn-premium-secondary-gradient text-sm disabled:opacity-50"
            >
              {syncKategoriLoading ? 'Duke transferuar...' : 'Transfero Të Gjitha Kategoritë'}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <input
                value={syncKategoriId}
                onChange={(e) => setSyncKategoriId(e.target.value)}
                placeholder="ID e Kategorisë"
                className="input-premium flex-1 text-sm"
              />
              <button
                onClick={() => handleSyncKategori('push-one')}
                disabled={syncKategoriLoading}
                className="btn-premium-secondary text-sm bg-dark-700 dark:bg-dark-600 text-white hover:bg-dark-800 dark:hover:bg-dark-500 disabled:opacity-50"
              >
                Transfero Një
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Madhësitë Sync - Admin Only */}
      {roli === 'admin' && (
        <div className="card-premium p-8 animate-slide-up">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Ruler size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            Madhësitë Sync
          </h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleSyncMadhesi('push-all')}
              disabled={syncMadhesiLoading}
              className="btn-premium-secondary-gradient text-sm disabled:opacity-50"
            >
              {syncMadhesiLoading ? 'Duke transferuar...' : 'Transfero Të Gjitha Madhësitë'}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <input
                value={syncMadhesiId}
                onChange={(e) => setSyncMadhesiId(e.target.value)}
                placeholder="ID e Madhësisë"
                className="input-premium flex-1 text-sm"
              />
              <button
                onClick={() => handleSyncMadhesi('push-one')}
                disabled={syncMadhesiLoading}
                className="btn-premium-secondary text-sm bg-dark-700 dark:bg-dark-600 text-white hover:bg-dark-800 dark:hover:bg-dark-500 disabled:opacity-50"
              >
                Transfero Një
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
